import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IncomingCallData } from '../../hooks/useTelephony';

interface Props {
  call: IncomingCallData;
  onDismiss: () => void;
  onAnswer?: () => void; // если WebRTC в состоянии ringing
  onAnswerAMI?: (extension: string) => Promise<boolean>; // для AMI ответа
  webrtcError?: string; // ошибка ответа через браузерный телефон
}

function playRingtone(): () => void {
  try {
    const ctx = new AudioContext();
    let stopped = false;

    const ring = () => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 480;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = 620;
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.2, ctx.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc2.start(ctx.currentTime);
      osc2.stop(ctx.currentTime + 0.5);

      setTimeout(() => ring(), 2000);
    };

    ring();
    let closed = false;
    return () => {
      if (closed) return;
      closed = true;
      stopped = true;
      ctx.close().catch(() => {});
    };
  } catch {
    return () => {};
  }
}

export default function IncomingCallPopup({ call, onDismiss, onAnswer, onAnswerAMI, webrtcError }: Props) {
  const navigate = useNavigate();
  const stopRingtone = useRef<() => void>(() => {});
  const [showExtensionInput, setShowExtensionInput] = useState(false);
  const [extension, setExtension] = useState(() => { try { return localStorage.getItem('answer_extension') || '771'; } catch { return '771'; } });
  const [answerError, setAnswerError] = useState('');
  const [isAnswering, setIsAnswering] = useState(false);

  useEffect(() => {
    stopRingtone.current = playRingtone();
    return () => stopRingtone.current();
  }, []);

  const handleOpenClient = () => {
    stopRingtone.current();
    if (call.clientId) navigate(`/clients/${call.clientId}`);
    onDismiss();
  };

  const handleOpenDeal = () => {
    stopRingtone.current();
    if (call.dealId) navigate(`/deals?deal=${call.dealId}`);
    onDismiss();
  };

  const handleDismiss = () => {
    stopRingtone.current();
    onDismiss();
  };

  const handleAnswer = () => {
    stopRingtone.current();
    if (onAnswer) {
      onAnswer();
      if (call.clientId) navigate(`/clients/${call.clientId}`);
    } else if (onAnswerAMI) {
      setShowExtensionInput(true);
    }
  };

  const handleConfirmAnswer = async () => {
    if (!onAnswerAMI) return;
    setAnswerError("");
    setIsAnswering(true);
    const success = await onAnswerAMI(extension.trim());
    setIsAnswering(false);
    if (success) {
      try { localStorage.setItem("answer_extension", extension.trim()); } catch {}
      setShowExtensionInput(false);
      if (call.clientId) navigate(`/clients/${call.clientId}`);
      onDismiss();
    } else {
      setAnswerError("Не удалось ответить: звонок уже завершён или неверный внутренний номер");
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 'calc(12px + env(safe-area-inset-top))',
      right: 12,
      left: 'auto',
      maxWidth: 'calc(100vw - 24px)',
      zIndex: 9999,
      background: '#1a1a2e',
      color: '#fff',
      borderRadius: 16,
      padding: '20px 24px',
      width: 360,
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      animation: 'slideInRight 0.3s ease',
    }}>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
        @keyframes ring {
          0%, 100% { transform: rotate(-8deg); }
          50%       { transform: rotate(8deg); }
        }
      `}</style>

      {/* Иконка + заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 32, animation: 'ring 0.5s ease infinite' }}>📞</span>
        <div>
          <div style={{ fontSize: 13, color: '#aaa', marginBottom: 2 }}>Входящий звонок</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{call.callerNumber}</div>
        </div>
        <button
          onClick={handleDismiss}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#aaa', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
        >×</button>
      </div>

      {/* Клиент */}
      {call.clientName ? (
        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            Клиент
            {call.clientCreated && (
              <span style={{ fontSize: 10, background: 'rgba(245,158,11,0.2)', color: '#f59e0b', padding: '1px 6px', borderRadius: 10 }}>
                новый номер — создан автоматически
              </span>
            )}
          </div>
          <div style={{ fontWeight: 600 }}>{call.clientName}</div>
          {call.dealNumber && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 12, color: '#7ec8e3' }}>
                {call.dealCreated ? '✨ Создана сделка: ' : 'Сделка: '}{call.dealNumber}
              </span>
              {call.dealCreated && (
                <span style={{ fontSize: 10, background: 'rgba(16,185,129,0.2)', color: '#10b981', padding: '1px 6px', borderRadius: 10 }}>
                  новая
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 13, color: '#f0a500' }}>⚠ Номер не распознан — клиент не создан</div>
        </div>
      )}

      {/* Ссылки на клиента/сделку */}
      {(call.clientId || call.dealId) && (
        <div style={{ display: 'flex', gap: 8 }}>
          {call.clientId && (
            <button onClick={handleOpenClient} style={btnStyle('#2563eb')}>
              Клиент
            </button>
          )}
          {call.dealId && (
            <button onClick={handleOpenDeal} style={btnStyle('#16a34a')}>
              К сделке
            </button>
          )}
        </div>
      )}

      {webrtcError && (
        <div style={{ fontSize: 12, color: '#f87171', background: 'rgba(239,68,68,0.12)', borderRadius: 6, padding: '8px 10px' }}>
          {webrtcError}
        </div>
      )}

      {answerError && (
        <div style={{ fontSize: 12, color: '#f87171', background: 'rgba(239,68,68,0.12)', borderRadius: 6, padding: '8px 10px' }}>
          {answerError}
        </div>
      )}

      {/* Ввод расширения */}
      {showExtensionInput && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={extension}
            onChange={(e) => setExtension(e.target.value)}
            placeholder="Ваше расширение"
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 14,
            }}
            disabled={isAnswering}
          />
          <button
            onClick={handleConfirmAnswer}
            disabled={isAnswering}
            style={{
              background: '#16a34a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 13,
              fontWeight: 600,
              cursor: isAnswering ? 'not-allowed' : 'pointer',
              opacity: isAnswering ? 0.6 : 1,
            }}
          >
            {isAnswering ? '...' : 'OK'}
          </button>
        </div>
      )}

      {/* Кнопки ответа / сброса */}
      {!showExtensionInput && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleAnswer}
            style={{
              flex: 1,
              background: (onAnswer || onAnswerAMI) ? '#16a34a' : '#374151',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px',
              fontSize: 20,
              cursor: 'pointer',
              fontWeight: 700,
            }}
            title={onAnswer ? 'Ответить на звонок (WebRTC)' : onAnswerAMI ? 'Ответить на звонок' : 'Открыть в CRM'}
          >
            📞
          </button>
          <button
            onClick={handleDismiss}
            style={{
              flex: 1,
              background: '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px',
              fontSize: 20,
              cursor: 'pointer',
              fontWeight: 700,
            }}
            title="Отклонить"
          >
            📵
          </button>
        </div>
      )}

      {/* Подсказка если не установлен способ ответа */}
      {!onAnswer && !onAnswerAMI && (
        <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center' }}>
          Откройте телефон (📞 внизу) для ответа через браузер
        </div>
      )}
    </div>
  );
}

function btnStyle(bg: string): React.CSSProperties {
  return {
    flex: 1,
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}
